import { collection, getDocs, getDoc, query, where, updateDoc, addDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { sessionStart, sessionLabel } from './dateUtils';
import { googleCalendarUrl, icsObjectUrl, icsFileName, addToDeviceCalendar } from './calendar';
import { toast, confirmSheet } from './components/feedback';
import { isNative, tapFeedback, successFeedback } from './native';

// Accept and decline a session request, and the calendar sheet. Used by Today and Sessions.

// The two-hour block a session covers.
export const SESSION_MS = 2 * 60 * 60 * 1000;
// "Join video" is the main action from 10 minutes before the start until the end.
export const inLiveWindow = (s, now = Date.now()) => {
  const t = sessionStart(s)?.getTime();
  return t != null && now >= t - 10 * 60 * 1000 && now < t + SESSION_MS;
};

const partnerOf = s => ((s.isMine ? s.toName : s.fromName) || 'your study partner').trim();

// Google Calendar and Apple / Outlook (.ics) — or Apple Calendar in the iPhone app.
export function offerCalendar(s, zone, title = 'Add to calendar') {
  const partner = partnerOf(s);
  const google = googleCalendarUrl(s, partner);
  if (!google) { toast('This session has no readable date.', { type: 'error' }); return; }
  const ics = isNative ? null : icsObjectUrl(s, partner);
  confirmSheet({
    title,
    message: `Your session with ${partner} on ${sessionLabel(s, zone)}.`,
    cancelLabel: 'Not now',
    choices: [
      { label: 'Google Calendar', value: 'google', href: google, icon: 'calendar' },
      isNative
        ? { label: 'Apple Calendar', value: 'apple', icon: 'calendar' }
        : { label: 'Apple / Outlook (.ics)', value: 'ics', href: ics, download: icsFileName(partner), icon: 'download' },
    ],
  }).then(choice => {
    if (ics) setTimeout(() => URL.revokeObjectURL(ics), 60000);
    if (choice === 'apple') addToDeviceCalendar(s, partner).catch(() => toast('Could not open the calendar.', { type: 'error' }));
  });
}

const notifyRequester = async (s, me, myName, type, verb) => {
  try {
    const recip = await getDoc(doc(db, 'users', s.fromUid));
    if (recip.exists() && recip.data()?.notificationPreferences?.studyRequests !== false) {
      await addDoc(collection(db, 'notifications'), {
        toUid: s.fromUid, fromUid: me, fromName: myName,
        type, text: `${myName} ${verb} your session request`,
        read: false, createdAt: serverTimestamp(),
      });
    }
  } catch (e) { console.warn('notif write failed', e); }
};

// Refuses a time that already has a confirmed session, then offers the calendar.
export async function acceptSession(s, { uid, myName, zone }) {
  try {
    const [mine, theirs] = await Promise.all([
      getDocs(query(collection(db, 'sessions'), where('fromUid', '==', uid))),
      getDocs(query(collection(db, 'sessions'), where('toUid', '==', uid))),
    ]);
    const start = sessionStart(s)?.getTime();
    const clash = [...mine.docs, ...theirs.docs].some(d => {
      if (d.id === s.id) return false;
      const o = d.data();
      if (o.status !== 'accepted') return false;
      const oStart = sessionStart(o)?.getTime();
      if (start && oStart) return start === oStart;
      if (s.startMin != null && o.startMin != null) return s.startMin === o.startMin;
      return o.slot === s.slot;
    });
    if (clash) { toast(`You already have a confirmed session at ${sessionLabel(s, zone)}. Decline this one or free up that time first.`, { type: 'error' }); return false; }
    await updateDoc(doc(db, 'sessions', s.id), { status: 'accepted' });
    successFeedback();
    offerCalendar(s, zone, 'Session confirmed');
    await notifyRequester(s, uid, myName, 'session_accepted', 'accepted');
    return true;
  } catch (e) {
    console.error(e);
    toast('Could not accept the session. Please try again.', { type: 'error' });
    return false;
  }
}

export async function declineSession(s, { uid, myName }) {
  try {
    await updateDoc(doc(db, 'sessions', s.id), { status: 'declined' });
    tapFeedback();
    await notifyRequester(s, uid, myName, 'session_declined', 'declined');
  } catch (e) {
    console.error(e);
    toast('Could not decline the session. Please try again.', { type: 'error' });
  }
}
